/**
 * Boot.
 *
 * Four things have to happen in this order, and each one is load-bearing:
 *
 *   1. `document.fonts.ready`, BEFORE the desktop exists. Webwin rasterizes glyphs into an atlas on
 *      first use and never evicts, so a frame drawn before a webfont has loaded caches the fallback
 *      font's glyphs for the life of the page.
 *   2. `Desktop.create`, which can throw `GpuUnavailableError`. That is the whole no-WebGPU story:
 *      the canvas is exactly what could not be started, so the fallback has to be real DOM, and it is
 *      already in the page waiting to be unhidden.
 *   3. The deep link is captured before authentication, so a pasted URL cannot mount a page the
 *      reader is not signed in to see.
 *   4. Sign-in is attempted three ways in the order the OpenSilver shell used: the API's
 *      debugger-only dev sign-in, then a stored refresh token, then the login page.
 */
import { Desktop, GpuUnavailableError } from 'webwin';
import * as auth from './api/auth.js';
import * as hub from './api/hub.js';
import { registerRoutes, Shell } from './app.js';
import * as router from './router.js';
import { CrmTheme } from './theme/CrmTheme.js';
import { isDark } from './theme/store.js';
import { APP_VERSION, stampDocumentTitle } from './version.js';

async function boot(): Promise<void> {
  stampDocumentTitle();
  auth.discardRetiredToken();

  const canvas = document.getElementById('crm');
  if (!(canvas instanceof HTMLCanvasElement)) throw new Error('No #crm canvas in the page.');

  // See (1) above. Never rejects in practice, but a browser that does not implement it must not stop
  // the app starting.
  try {
    await document.fonts.ready;
  } catch {
    /* proceed with whatever the atlas rasterizes */
  }

  let desktop: Desktop;
  try {
    desktop = await Desktop.create(canvas, { theme: new CrmTheme(isDark()) });
  } catch (error) {
    if (error instanceof GpuUnavailableError) {
      showNoGpu(error.message);
      return;
    }
    throw error;
  }

  const shell = new Shell(desktop);
  shell.page.show(desktop);

  registerRoutes();
  shell.start();

  // See (3). Must run before the first navigation.
  router.capture();

  // Exposed for driving the app from the browser console and from a verification run. There is no
  // DOM to inspect on a canvas, so without a handle like this the UI cannot be examined at all - and
  // it is gated to localhost, because a global that can navigate the app and read the session has no
  // business existing in production.
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
    (window as unknown as Record<string, unknown>).__crm = {
      desktop,
      shell,
      router,
      auth,
      hub,
      version: APP_VERSION
    };
  }

  // `start` performs the first navigation and returns its render, so there is no second one to
  // await here: rendering again would mount the landing page twice and abort its own first load.
  await start();
  shell.syncSelection();

  await announceBuild();
}

/**
 * Where to land once authentication is settled.
 *
 * The same three-way routing `MainPage.StartAsync` does. Dev sign-in first: in production that
 * endpoint is a 404, so this falls through on its own with no environment check of its own.
 */
async function start(): Promise<void> {
  if (await auth.tryDevSignIn()) {
    await router.start();
    return;
  }

  // A stored refresh token survives F5. A restored bootstrap account still owes a password change,
  // which mirrors the login page's own routing.
  if (await auth.tryRestoreSession()) {
    if (auth.mustChangePassword()) await router.navigate('/change-password', true);
    else await router.start();
    return;
  }

  await router.navigate('/login', true);
}

/**
 * Logs which bundle this is.
 *
 * The other half of the `?v=` content stamp: a content hash stops a stale bundle being served, and
 * this makes it answerable from the console which one actually ran - the check for "I fixed it and
 * nothing changed", which on this repo is usually a stale bundle rather than a wrong fix.
 */
async function announceBuild(): Promise<void> {
  let built = '';
  try {
    const response = await fetch('/app/build.json', { cache: 'no-store' });
    if (response.ok) built = ((await response.json()) as { builtUtc?: string }).builtUtc ?? '';
  } catch {
    /* the log line is a convenience, not a requirement */
  }
  console.info(`JetsonCRM app ${APP_VERSION}${built ? ` built ${built}` : ''}`);
}

/** Reveals the no-WebGPU notice that is already in index.html. */
function showNoGpu(detail: string): void {
  const panel = document.getElementById('nogpu');
  const detailNode = document.getElementById('nogpu-detail');
  if (detailNode) detailNode.textContent = detail;
  if (panel) panel.hidden = false;
  const canvas = document.getElementById('crm');
  if (canvas) canvas.remove();
  console.warn(`JetsonCRM app ${APP_VERSION}: WebGPU unavailable - ${detail}`);
}

void boot().catch((error: unknown) => {
  console.error('JetsonCRM app failed to start', error);
  // Reuses the fallback panel: whatever went wrong, a blank canvas tells the reader nothing, and this
  // is the one screen guaranteed not to depend on the part that just failed.
  showNoGpu(error instanceof Error ? error.message : String(error));
});
